import { useState, useEffect } from "react";
import { fetchPastEvents } from "src/api/payload/pastEvents";
import { getSinglePastEventData } from "src/utils/tournamentUtils";
import {
  isPayloadPastEventsSource,
  normalizePastEventsLocale,
} from "src/utils/pastEventsSource";

/**
 * Resolves a single past event for the winners detail page.
 *
 * Payload source: reuses the same `fetchPastEvents` list request (one
 * request total, matches today's scan-all-years behavior — see
 * docs/PAST_EVENTS_CMS_INTEGRATION_PLAN.md "Open Decisions") and finds the
 * event by localized `slug` (`:eventSlug` route param).
 *
 * Falls back silently to `getSinglePastEventData` (pathname-based static
 * lookup, unchanged) on flag-off, empty/error CMS response, or a slug that
 * isn't found in the CMS list.
 *
 * @param {string} eventSlug - `useParams().eventSlug` from the route
 * @param {string} pathname - `useLocation().pathname`, for the static-fallback match
 * @param {string} locale - i18n language (`en` | `fr`)
 * @returns {{
 *   event: import('src/utils/mapPastEvents').PastEvent | null,
 *   loading: boolean,
 *   error: string | null,
 *   source: 'payload' | 'static',
 * }}
 */
export function usePastEvent(eventSlug, pathname, locale) {
  const isPayloadSource = isPayloadPastEventsSource();
  const normalizedLocale = normalizePastEventsLocale(locale);
  const [event, setEvent] = useState(null);
  const [source, setSource] = useState(isPayloadSource ? "payload" : "static");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadStatic = async () => {
      try {
        const staticEvent = await getSinglePastEventData(
          { pathname },
          { language: normalizedLocale }
        );
        if (cancelled) return;
        setEvent(staticEvent || null);
        setSource("static");
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching static past event:", err);
        setEvent(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const load = async () => {
      setLoading(true);
      setError(null);

      if (isPayloadSource && eventSlug) {
        try {
          const cmsEvents = await fetchPastEvents(normalizedLocale);
          if (cancelled) return;
          const match = cmsEvents.find((e) => e.slug === eventSlug);
          if (match) {
            setEvent(match);
            setSource("payload");
            setLoading(false);
            return;
          }
        } catch (err) {
          if (cancelled) return;
          console.error("Error fetching past events from Payload:", err);
          setError(err?.message || "Failed to load past event");
        }
      }

      await loadStatic();
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isPayloadSource, eventSlug, pathname, normalizedLocale]);

  return { event, loading, error, source };
}
